/**
 * Client-side Timezone Utilities
 * 
 * Provides timezone detection and date formatting for the Vue.js frontend
 */

/**
 * Get user's timezone using Intl API
 */
export const getUserTimezone = () => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'Asia/Kolkata'
  } catch (error) {
    console.warn('Failed to detect user timezone:', error)
    return 'Asia/Kolkata'
  }
}

/**
 * Get default timezone from environment
 */
export const getDefaultTimezone = () => {
  return import.meta.env.VITE_DEFAULT_TIMEZONE || 'Asia/Kolkata'
}

/**
 * Check if timezone detection is enabled
 */
export const isTimezoneDetectionEnabled = () => {
  return import.meta.env.VITE_ENABLE_TIMEZONE_DETECTION === 'true'
}

/**
 * Get the timezone to use for the application
 */
export const getAppTimezone = (businessTimezone = null) => {
  // Priority: Business timezone > User timezone (if detection enabled) > Default timezone
  
  if (businessTimezone) {
    return businessTimezone
  }
  
  if (isTimezoneDetectionEnabled()) {
    return getUserTimezone()
  }
  
  return getDefaultTimezone()
}

/**
 * Format date in Indian format (DD/MM/YYYY)
 */
export const formatIndianDate = (date, timezone = null) => {
  const tz = timezone || getAppTimezone()
  
  try {
    return new Intl.DateTimeFormat('en-IN', {
      timeZone: tz,
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    }).format(new Date(date))
  } catch (error) {
    console.warn('Date formatting error:', error)
    return new Date(date).toLocaleDateString('en-IN')
  }
}

/**
 * Format date and time in Indian format (DD/MM/YYYY, HH:MM AM/PM)
 */
export const formatIndianDateTime = (date, timezone = null) => {
  const tz = timezone || getAppTimezone()
  
  try {
    return new Intl.DateTimeFormat('en-IN', {
      timeZone: tz,
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    }).format(new Date(date))
  } catch (error) {
    console.warn('DateTime formatting error:', error)
    return new Date(date).toLocaleString('en-IN')
  }
}

/**
 * Format date for invoice display (DD MMM YYYY)
 */
export const formatInvoiceDate = (date, timezone = null) => {
  const tz = timezone || getAppTimezone()
  
  try {
    return new Intl.DateTimeFormat('en-IN', {
      timeZone: tz,
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    }).format(new Date(date))
  } catch (error) {
    console.warn('Invoice date formatting error:', error)
    return new Date(date).toLocaleDateString('en-IN')
  }
}

/**
 * Get current date in business timezone
 */
export const getCurrentBusinessDate = (timezone = null) => {
  const tz = timezone || getAppTimezone()
  
  try {
    // Get current time in business timezone
    const now = new Date()
    const formatter = new Intl.DateTimeFormat('en-CA', {
      timeZone: tz,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    })
    
    const parts = formatter.formatToParts(now)
    const year = parts.find(p => p.type === 'year').value
    const month = parts.find(p => p.type === 'month').value
    const day = parts.find(p => p.type === 'day').value
    
    return new Date(`${year}-${month}-${day}T00:00:00`)
  } catch (error) {
    console.warn('Business date calculation error:', error)
    return new Date()
  }
}

/**
 * Get start and end of day in business timezone
 */
export const getBusinessDayBounds = (date = null, timezone = null) => {
  const tz = timezone || getAppTimezone()
  const baseDate = date || new Date()
  
  try {
    // Get date parts in business timezone
    const formatter = new Intl.DateTimeFormat('en-CA', {
      timeZone: tz,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    })
    
    const parts = formatter.formatToParts(baseDate)
    const year = parts.find(p => p.type === 'year').value
    const month = parts.find(p => p.type === 'month').value
    const day = parts.find(p => p.type === 'day').value
    
    const startOfDay = new Date(`${year}-${month}-${day}T00:00:00`)
    const endOfDay = new Date(`${year}-${month}-${day}T23:59:59.999`)
    
    return { startOfDay, endOfDay }
  } catch (error) {
    console.warn('Business day bounds calculation error:', error)
    const start = new Date(baseDate)
    start.setHours(0, 0, 0, 0)
    const end = new Date(baseDate)
    end.setHours(23, 59, 59, 999)
    return { startOfDay: start, endOfDay: end }
  }
}

/**
 * Convert local time to business timezone for API calls
 */
export const toBusinessTime = (date, timezone = null) => {
  const tz = timezone || getAppTimezone()
  
  try {
    // Create a new date object and format it for the business timezone
    const businessTime = new Date(date.toLocaleString('en-US', { timeZone: tz }))
    return businessTime
  } catch (error) {
    console.warn('Business time conversion error:', error)
    return new Date(date)
  }
}

/**
 * Add timezone header to API requests
 */
export const getTimezoneHeaders = (businessTimezone = null) => {
  const timezone = businessTimezone || getUserTimezone()
  
  return {
    'X-Timezone': timezone
  }
}

/**
 * List of supported timezones for dropdown
 */
export const getSupportedTimezones = () => {
  return [
    { 
      value: 'Asia/Kolkata', 
      label: 'India Standard Time (IST)', 
      offset: '+05:30',
      description: 'Mumbai, Delhi, Kolkata, Chennai'
    },
    { 
      value: 'Asia/Dubai', 
      label: 'Gulf Standard Time (GST)', 
      offset: '+04:00',
      description: 'Dubai, Abu Dhabi'
    },
    { 
      value: 'America/New_York', 
      label: 'Eastern Time (ET)', 
      offset: '-05:00',
      description: 'New York, Miami'
    },
    { 
      value: 'America/Chicago', 
      label: 'Central Time (CT)', 
      offset: '-06:00',
      description: 'Chicago, Dallas'
    },
    { 
      value: 'America/Denver', 
      label: 'Mountain Time (MT)', 
      offset: '-07:00',
      description: 'Denver, Phoenix'
    },
    { 
      value: 'America/Los_Angeles', 
      label: 'Pacific Time (PT)', 
      offset: '-08:00',
      description: 'Los Angeles, San Francisco'
    },
    { 
      value: 'Europe/London', 
      label: 'Greenwich Mean Time (GMT)', 
      offset: '+00:00',
      description: 'London, Edinburgh'
    },
    { 
      value: 'Europe/Paris', 
      label: 'Central European Time (CET)', 
      offset: '+01:00',
      description: 'Paris, Berlin, Rome'
    },
    { 
      value: 'Asia/Tokyo', 
      label: 'Japan Standard Time (JST)', 
      offset: '+09:00',
      description: 'Tokyo, Osaka'
    },
    { 
      value: 'Asia/Shanghai', 
      label: 'China Standard Time (CST)', 
      offset: '+08:00',
      description: 'Beijing, Shanghai'
    },
    { 
      value: 'Australia/Sydney', 
      label: 'Australian Eastern Time (AET)', 
      offset: '+10:00',
      description: 'Sydney, Melbourne'
    }
  ]
}

/**
 * Validate timezone string
 */
export const isValidTimezone = (timezone) => {
  try {
    Intl.DateTimeFormat(undefined, { timeZone: timezone })
    return true
  } catch (error) {
    return false
  }
}

/**
 * Get timezone offset string (+05:30 format)
 */
export const getTimezoneOffset = (timezone = null) => {
  const tz = timezone || getAppTimezone()
  
  try {
    const now = new Date()
    const offset = new Intl.DateTimeFormat('en', {
      timeZone: tz,
      timeZoneName: 'longOffset'
    }).formatToParts(now).find(part => part.type === 'timeZoneName')?.value
    
    return offset || '+05:30'
  } catch (error) {
    console.warn('Timezone offset calculation error:', error)
    return '+05:30'
  }
}